import React, { useContext, useMemo, useState } from "react";
import style from "./index.module.scss";
import {
    Button,
    Checkbox,
    Dropdown,
    Icon,
    Menu,
    message,
    Modal,
    Spin
} from "antd";
import useModalHandler from "utils/v2/useModalHandler";
import useRequest from "utils/useRequest";
import { CourseContext } from "../Store";
import CustomEmpty from "components/CourseComponents/CustomEmpty";
import LocalModal from "./Components/LocalModal";
import HrefModal from "./Components/HrefModal";
import {
    DeleteProps,
    deleteSource,
    downloadCourseWare,
    getSourceList,
    SourceItem,
    updateSourceName
} from "../../ResourcePool/service";
import useCurrentDate from "../../../utils/useCurrentDate";
import ListItemV2 from "../../../components/CourseComponents/Resource/ListItem";
import {
    pulishCoursewareSource,
    RES_EVENT_PARAMS
} from "../../../service/CourseDetail/resource";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import useRequestFn from "../../../utils/useRequestFn";
import { ResultMessage } from "../../../components/ResultMessage";
import { Can, errorMessage } from "../../../service/service";
import VideoModal from "./Components/VideoModal";
import ResourceModal from "./Components/ResourceModal";
import { useHistory } from "react-router-dom";
import PreviewSourceModal from "../../../components/PreviewSourceModal";
import DeleteConfirm from "../../../components/DeleteConfirm";
import { EventBus } from "../../../utils/eventBus";
import { useMount, useUnmount } from "react-use";
import StuViewModal from "./Components/StuViewModal";
import ReNameModal from "../../../components/CourseComponents/Resource/ListItem/Components/ReNameModal";
import EditModal from "./Components/EditModal";

export default function Resource() {
    const { course } = useContext(CourseContext);
    const { batchNo } = useCurrentDate();
    const [data, { loading }, reload] = useRequest(() =>
        getSourceList({
            batchNo,
            courseNo: course.courseNo,
            courseSeq: course.courseSeq,
            delStatus: "",
            keyword: "",
            pageNo: 1,
            pageSize: 9999,
            weektimeId: course.weekTimeId
        })
    );

    function reloadAll() {
        setSelect([]);
        reload();
    }

    //本地文件
    const localModal = useModalHandler(LocalModal, { remove: true });
    function openLocalModal() {
        localModal.open();
    }
    function onLocalSuccess() {
        localModal.close();
        reloadAll();
    }

    //网页链接
    const hrefModal = useModalHandler(HrefModal);
    function openHrefModal() {
        hrefModal.open();
    }
    function onHrefSuccess() {
        hrefModal.close();
        reloadAll();
    }

    //直播回放
    const videoModal = useModalHandler(VideoModal);
    function openVideoModal() {
        videoModal.open();
    }
    function onVideoSuccess() {
        videoModal.close();
        reloadAll();
    }

    //资源库选择
    const resModal = useModalHandler(ResourceModal);
    function openResModal() {
        resModal.open();
    }
    function onResSuccess() {
        resModal.close();
        reloadAll();
    }

    //修改
    const editModal = useModalHandler(EditModal);
    function openEditModal(source: SourceItem) {
        editModal.open();
        editModal.setPayload(source);
    }
    function onEditSuccess() {
        editModal.close();
        reloadAll();
    }

    //选择 checkbox
    const [select, setSelect] = useState<CheckboxValueType[]>([]);
    const onCheckAllChange = (e: CheckboxChangeEvent) => {
        const plainOptions = data?.data.map(item => item.id.toString());
        setSelect(e.target.checked ? plainOptions || [] : []);
    };
    const checkAll = useMemo(() => {
        return select.length === data?.data?.length && select.length > 0;
    }, [select, data?.data]);

    //改变发布状态
    const [changePublicAction, { loading: changeLoading }] = useRequestFn(
        pulishCoursewareSource
    );
    async function onPublicChange(id: string, isPublish: Can) {
        try {
            await changePublicAction({
                id,
                isPublish
            });
            await reloadAll();
            message.success("改变发布状态成功");
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    //批量发布/取消发布
    function PublicChangeMany(isPublish: Can) {
        onPublicChange(select.join(","), isPublish);
    }

    //下拉菜单
    const menu = (
        <Menu>
            <Menu.Item onClick={openLocalModal}>本地文件</Menu.Item>
            <Menu.Item onClick={openVideoModal}>直播回放</Menu.Item>
            <Menu.Item onClick={openHrefModal}>网页链接</Menu.Item>
            <Menu.Item onClick={openResModal}>资源库选择</Menu.Item>
        </Menu>
    );

    //下载
    async function onDownload(fileId: number) {
        try {
            await downloadCourseWare({
                fileId
            });
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    //投屏
    const history = useHistory();
    function onProjection(id: number) {
        history.push(
            `/course/projection/${course.weekTimeId}/resource?id=${id}`
        );
    }

    //删除
    const [deleteAction, { loading: deleteLoading }] =
        useRequestFn(deleteSource);
    async function onDelete(params: DeleteProps[]) {
        try {
            await deleteAction(params);
            await reloadAll();
            message.success("删除成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }
    //批量删除
    function DeleteMany() {
        Modal.confirm({
            content: "确认删除课件?",
            onOk: async () => {
                const params = select.map(item => {
                    return {
                        deleteFromSource: "",
                        sourceId: item.toString(),
                        type: "1"
                    };
                });
                if (params.length > 0) {
                    await onDelete(params);
                }
            }
        });
    }

    //下拉菜单
    const actionMenu = (
        <Menu>
            <Menu.Item disabled={select.length === 0} onClick={DeleteMany}>
                删除
            </Menu.Item>
            <Menu.Item onClick={() => PublicChangeMany(Can.yes)}>
                发布
            </Menu.Item>
            <Menu.Item>
                <DeleteConfirm
                    title="确定取消发布课件吗？"
                    content="取消发布课件后，学习统计数据将会被清空，请谨慎操作。"
                    onSuccess={() => {
                        PublicChangeMany(Can.no);
                    }}>
                    <span>取消发布</span>
                </DeleteConfirm>
            </Menu.Item>
        </Menu>
    );

    //预览
    const prevModal = useModalHandler(PreviewSourceModal);
    function onPrev(id: number) {
        prevModal.setPayload(id);
        prevModal.open();
    }

    const stuViewModal = useModalHandler(StuViewModal, { remove: true });

    //学生视角预览
    useMount(() => {
        //target： src/pages/CourseDetail/components/CourseLayout/components/CourseHeader/index.tsx
        EventBus.on(
            RES_EVENT_PARAMS.STUDENT_PERSPECTIVE_PREVIEW_OPEN,
            stuViewModal.open
        );
    });
    useUnmount(() => {
        EventBus.removeAllListeners(
            RES_EVENT_PARAMS.STUDENT_PERSPECTIVE_PREVIEW_OPEN
        );
    });

    const reNameModal = useModalHandler(ReNameModal);
    const [reNameAction] = useRequestFn(updateSourceName);
    function openReNameModal(sourceName: string, id: number) {
        reNameModal.open();
        reNameModal.setPayload({ sourceName, id });
    }
    async function onReName(sourceName: string, sourceId: number) {
        try {
            await reNameAction({
                batchNo: batchNo,
                courseNo: course.courseNo,
                courseSeq: course.courseSeq,
                sourceId,
                sourceName
            });
            await reloadAll();
            reNameModal.close();
            message.success("重命名成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    return (
        <div className={style.root}>
            <div className={style.header}>
                <div className={style.count}>
                    课件（{data?.totalSize || 0}）
                    <a
                        href="http://go.plvideo.cn/front/video/view?vid=5c0ad4c56c726eba25fb54f255d7fd06_5"
                        target="_blank"
                        className={style.tip}
                        rel="noreferrer">
                        <Icon type="play-square" /> 点击查看使用说明视频
                    </a>
                </div>

                <div className={style.tools}>
                    <Dropdown
                        disabled={select.length === 0}
                        overlay={actionMenu}
                        placement="bottomCenter">
                        <Button style={{ borderRadius: 2 }}>
                            批量操作
                            <Icon type="down" />
                        </Button>
                    </Dropdown>
                    <Dropdown overlay={menu} placement="bottomCenter">
                        <Button style={{ borderRadius: 2 }}>
                            添加
                            <Icon type="down" />
                        </Button>
                    </Dropdown>
                </div>
            </div>

            <Spin
                spinning={[loading, changeLoading, deleteLoading].some(
                    item => item
                )}>
                <div className={style.main}>
                    <dl className={style.dl}>
                        <dt className={style.dt}>
                            <Checkbox
                                onChange={onCheckAllChange}
                                indeterminate={select.length > 0 && !checkAll}
                                checked={checkAll}
                            />
                        </dt>
                        <dd className={style.dd}>全选</dd>
                    </dl>
                    <Checkbox.Group
                        style={{ width: "100%" }}
                        value={select}
                        onChange={setSelect}>
                        {data?.data &&
                            data?.data?.map(item => (
                                <dl key={item.id} className={style.dl}>
                                    <dt className={style.dt}>
                                        <Checkbox value={item.id} />
                                    </dt>
                                    <dd className={style.dd}>
                                        <ListItemV2
                                            onPrev={onPrev}
                                            onEdit={openEditModal}
                                            onReName={openReNameModal}
                                            onProjection={onProjection}
                                            onPublicChange={onPublicChange}
                                            onDownload={onDownload}
                                            onDelete={sourceId => {
                                                onDelete([
                                                    {
                                                        deleteFromSource: "",
                                                        sourceId: sourceId,
                                                        type: "1"
                                                    }
                                                ]);
                                            }}
                                            source={item}
                                        />
                                    </dd>
                                </dl>
                            ))}
                    </Checkbox.Group>

                    {data?.data?.length === 0 && (
                        <CustomEmpty style={{ padding: "100px 0" }} />
                    )}
                </div>
            </Spin>

            {localModal.render(
                <LocalModal onSuccess={onLocalSuccess} width={610} />
            )}
            {hrefModal.render(
                <HrefModal onSuccess={onHrefSuccess} width={610} />
            )}
            {videoModal.render(<VideoModal onSuccess={onVideoSuccess} />)}
            {stuViewModal.render(<StuViewModal width={1000} />)}
            {resModal.render(<ResourceModal onSuccess={onResSuccess} />)}
            {editModal.render(<EditModal onSuccess={onEditSuccess} />)}
            {prevModal.render(<PreviewSourceModal data={data?.data || []} />)}
            {reNameModal.render(
                <ReNameModal
                    onSuccess={(sourceName, id) => onReName?.(sourceName, id)}
                />
            )}
        </div>
    );
}
